import useLocalStorage from '@hooks/use-local-storage';
import type { Role } from '@interfaces/role';
import { resetSessionNoticeDismissals } from '@utils/session-notice-dismissals';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo } from 'react';

export interface User extends Record<string, unknown> {
  id: number;
  token: string;
  username: string;
  full_name: string;
  organization_name: string;
  roles: Role[];
  terms_acceptance_required?: boolean;
  terms_version?: string;
  terms_url?: string;
  source?: TermsAcceptanceSource;
}

export type TermsAcceptanceSource = 'admin_login' | 'agent_login';

export interface TermsAcceptanceStatus extends Record<string, unknown> {
  terms_acceptance_required: boolean;
  terms_version: string;
  terms_url: string;
  source: TermsAcceptanceSource;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  signIn: (user: User) => void;
  signOut: () => void;
  updateTermsAcceptance: (status: TermsAcceptanceStatus) => void;
  getToken: () => string | undefined;
  getUsername: () => string | undefined;
  getAuthorName: () => string | undefined;
  getOrganizationName: () => string | undefined;
  getUserId: () => number | undefined;
  getTermsAcceptanceStatus: () => TermsAcceptanceStatus | undefined;
  requiresTermsAcceptance: () => boolean;
  hasRole: (role: Role) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useLocalStorage<User | undefined>('user', undefined);

  const isAuthenticated = currentUser !== undefined;

  const signIn = useCallback(
    (user: User) => {
      setCurrentUser(user);
    },
    [setCurrentUser],
  );

  const signOut = useCallback(() => {
    resetSessionNoticeDismissals();
    setCurrentUser(undefined);
  }, [setCurrentUser]);

  const updateTermsAcceptance = useCallback(
    (status: TermsAcceptanceStatus) => {
      setCurrentUser((user) => (user ? { ...user, ...status } : user));
    },
    [setCurrentUser],
  );

  const getToken = useCallback(() => {
    return currentUser?.token;
  }, [currentUser]);

  const getUsername = useCallback(() => {
    return currentUser?.username;
  }, [currentUser]);

  const getAuthorName = useCallback(() => {
    return currentUser?.full_name;
  }, [currentUser]);

  const getOrganizationName = useCallback(() => {
    return currentUser?.organization_name;
  }, [currentUser]);

  const getUserId = useCallback(() => {
    return currentUser?.id;
  }, [currentUser]);

  const getTermsAcceptanceStatus = useCallback(() => {
    const { terms_acceptance_required, terms_version, terms_url, source } = currentUser ?? {};

    if (terms_acceptance_required === undefined || !terms_version || !terms_url || !source) {
      return undefined;
    }

    return {
      terms_acceptance_required,
      terms_version,
      terms_url,
      source,
    };
  }, [currentUser]);

  const requiresTermsAcceptance = useCallback(() => {
    return currentUser?.terms_acceptance_required === true;
  }, [currentUser]);

  const hasRole = useCallback(
    (role: Role) => {
      return currentUser?.roles?.includes(role) || false;
    },
    [currentUser],
  );

  const value = useMemo(
    () => ({
      isAuthenticated,
      signIn,
      signOut,
      updateTermsAcceptance,
      getToken,
      getUsername,
      getAuthorName,
      getOrganizationName,
      getUserId,
      getTermsAcceptanceStatus,
      requiresTermsAcceptance,
      hasRole,
    }),
    [
      isAuthenticated,
      signIn,
      signOut,
      updateTermsAcceptance,
      getToken,
      getUsername,
      getAuthorName,
      getOrganizationName,
      getUserId,
      getTermsAcceptanceStatus,
      requiresTermsAcceptance,
      hasRole,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
